import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import "@/App.css";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import Home from "@/pages/marketing/Home";
import About from "@/pages/marketing/About";
import Membership from "@/pages/marketing/Membership";
import Features from "@/pages/marketing/Features";
import FeatureDetail from "@/pages/marketing/FeatureDetail";
import Resources from "@/pages/marketing/Resources";
import FAQsPage from "@/pages/marketing/FAQsPage";
import Onboarding from "@/pages/Onboarding";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Portal from "@/pages/Portal";
import CastlesAI from "@/pages/CastlesAI";
import PackingChecklist from "@/pages/PackingChecklist";
import AuthCallback from "@/components/AuthCallback";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminStudent from "@/pages/admin/AdminStudent";
import AdminCMS from "@/pages/admin/AdminCMS";
import AdminKnowledge from "@/pages/admin/AdminKnowledge";
import AdminSupport from "@/pages/admin/AdminSupport";

const Loader = () => (
  <div className="min-h-screen bg-[#060D1F] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
  </div>
);

function RequireAuth({ children, admin }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loader />;
  if (!user) return <Navigate to={admin ? "/admin/login" : "/login"} state={{ from: location.pathname }} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/portal" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/features" element={<Features />} />
        <Route path="/features/:slug" element={<FeatureDetail />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/faqs" element={<FAQsPage />} />
      </Route>
      <Route path="/packing" element={<Navigate to="/onboarding?campaign=packing" replace />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/verify-email" element={<RequireAuth><VerifyEmail /></RequireAuth>} />
      <Route path="/portal" element={<RequireAuth><Portal /></RequireAuth>} />
      <Route path="/portal/ai" element={<RequireAuth><CastlesAI /></RequireAuth>} />
      <Route path="/portal/packing" element={<RequireAuth><PackingChecklist /></RequireAuth>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<RequireAuth admin><AdminDashboard /></RequireAuth>} />
      <Route path="/admin/leads" element={<RequireAuth admin><AdminLeads /></RequireAuth>} />
      <Route path="/admin/students/:userId" element={<RequireAuth admin><AdminStudent /></RequireAuth>} />
      <Route path="/admin/cms" element={<RequireAuth admin><AdminCMS /></RequireAuth>} />
      <Route path="/admin/knowledge" element={<RequireAuth admin><AdminKnowledge /></RequireAuth>} />
      <Route path="/admin/support" element={<RequireAuth admin><AdminSupport /></RequireAuth>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
